import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { CacheKeys, ReferralWithScope_UserWithoutId } from '../../helpers/DataStructures';
import DateTimeFormat from '../../helpers/DateTimeFormat';
import { translateFromStatusToShortTextForReferrer } from '../../helpers/Language';
import { getReferralUnreadCount } from '../../helpers/referralUnread';
import Busy from '../../components/Busy';
import ReferralUnreadBadge from './ReferralUnreadBadge';
import ReferralRouteShell from './ReferralRouteShell';

const ReferralsSentPage: React.FC = () => {
  const apiServiceInstance = useAPIAuthenticatedService();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const backTo = `${location.pathname}${location.search}${location.hash}`;

  const { isLoading, isError, data } = useQuery(
    [CacheKeys.REFERRALS_FROM_USER],
    () => apiServiceInstance.getUserReferralsOut(),
    { keepPreviousData: true }
  );

  return (
    <ReferralRouteShell backTo="/dashboard" title="Referrals Sent">
      {isLoading || data === undefined ? (
        <Busy message="Loading referrals..." />
      ) : isError ? (
        <Typography>Unable to load sent referrals.</Typography>
      ) : data.length === 0 ? (
        <Typography>No referrals sent yet</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {data.map((referral: ReferralWithScope_UserWithoutId) => (
                <TableRow
                  key={referral.id}
                  onClick={() =>
                    navigate(`/dashboard/referrals/out/${referral.id}`, {
                      // REQ-019: referral details should return to the actual originating list when entered in-app.
                      state: { backTo },
                    })
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    {referral.offer.user.companyName
                      ? referral.offer.user.companyName
                      : `${referral.offer.user.givenName} ${referral.offer.user.familyName}`}
                  </TableCell>
                  <TableCell>{DateTimeFormat(referral.created)}</TableCell>
                  <TableCell>{referral.theReferralName}</TableCell>
                  <TableCell>
                    <Stack spacing={0.75}>
                      <ReferralUnreadBadge count={getReferralUnreadCount(referral)} />
                      <Box
                        sx={{
                          display: 'inline-flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          alignSelf: 'flex-start',
                        }}
                      >
                        <Typography component="span" variant="inherit">
                          {translateFromStatusToShortTextForReferrer(referral.status)}
                        </Typography>
                        {referral.status === 'succeeded' ? (
                          <Typography
                            component="span"
                            data-testid={`sent-referral-reward-${referral.id}`}
                            sx={{
                              // REQ-021: center the earned fee under the success label rather than the whole column.
                              color: 'primary.main',
                              fontWeight: 700,
                              textAlign: 'center',
                            }}
                            variant="body2"
                          >
                            +{'\u00A3'}{referral.offer.offeringFixedCashAmount}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Button
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/dashboard/referrals/out/${referral.id}`, {
                            state: { backTo },
                          });
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ReferralRouteShell>
  );
};

export default ReferralsSentPage;
